import {
  useState,
  useCallback,
  useMemo,
  useRef,
  useDeferredValue,
  useId,
  useTransition,
} from "react";
import { track } from "@/lib/tracking";
import type { LeadInput } from "@/lib/schemas";

type SimulatorProps = {
  onLead: (vertical: LeadInput["vertical"], valor: number) => void;
};

type SimVertical = "veiculos" | "imoveis" | "agro" | "servicos";

const TIPO_LABELS: Record<SimVertical, string> = {
  veiculos: "Veículos",
  imoveis: "Imóveis",
  agro: "Agro",
  servicos: "Serviços",
};

const RANGES: Record<SimVertical, { min: number; max: number; step: number }> =
  {
    veiculos: { min: 30000, max: 200000, step: 5000 },
    imoveis: { min: 100000, max: 2000000, step: 50000 },
    agro: { min: 100000, max: 1200000, step: 50000 },
    servicos: { min: 10000, max: 100000, step: 5000 },
  };

const formatBRL = (value: number) => value.toLocaleString("pt-BR");

/**
 * Card de simulador de consórcio (4 verticais + slider de valor).
 * Ao "Simular", abre LeadDialog pré-preenchido via `onLead`.
 * Dispara events: `simulator_started` (primeira interação) + `simulator_completed`.
 */
export function Simulator({ onLead }: SimulatorProps) {
  const [tipo, setTipo] = useState<SimVertical>("veiculos");
  const [valor, setValor] = useState(80000);
  const [nome, setNome] = useState("");
  const [, startTransition] = useTransition();
  // useDeferredValue suaviza o re-render do cálculo de parcela enquanto o
  // slider é arrastado (evita jank em CPU baixo / throttling 4x).
  const valorDeferred = useDeferredValue(valor);
  const reactId = useId();
  const idValor = `${reactId}-valor`;
  const idNome = `${reactId}-nome`;
  const idResumo = `${reactId}-resumo`;

  const range = RANGES[tipo];
  const parcela = useMemo(
    () => Math.round((valorDeferred / 200) * 0.62),
    [valorDeferred]
  );

  const startedRef = useRef(false);
  const markStarted = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    track("simulator_started", { vertical: tipo, valor });
  }, [tipo, valor]);

  const handleSimular = useCallback(() => {
    track("simulator_completed", {
      vertical: tipo,
      valor,
      has_name: !!nome,
    });
    onLead(tipo, valor);
  }, [tipo, valor, nome, onLead]);

  return (
    <div className="bg-white rounded-[var(--radius-xl-token)] shadow-[var(--shadow-elevated)] border border-gray-100 p-6 sm:p-8 w-full max-w-md">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-[var(--sr-navy)]">
          Simule seu consórcio
        </h2>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--sr-gold-ink)]">
          <span className="sr-pulse-dot" /> 2 min
        </span>
      </div>
      <p className="text-gray-500 text-xs mb-5">
        Resultado em 2 minutos, sem compromisso.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        {(Object.entries(TIPO_LABELS) as [SimVertical, string][]).map(
          ([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                startTransition(() => {
                  setTipo(key);
                  setValor(RANGES[key].min);
                });
                markStarted();
              }}
              className={`text-xs font-medium py-2 px-2 rounded-[var(--radius-sm-token)] border transition-all sr-focus-ring ${
                tipo === key
                  ? "bg-[var(--sr-navy)] text-white border-[var(--sr-navy)]"
                  : "bg-gray-50 text-gray-600 border-gray-200 hover:border-[var(--sr-navy)]/30"
              }`}
              aria-pressed={tipo === key}
            >
              {label}
            </button>
          )
        )}
      </div>

      <label
        className="block text-xs font-medium text-gray-500 mb-2"
        htmlFor={idValor}
      >
        Valor do crédito
      </label>
      <div className="mb-1">
        <input
          id={idValor}
          type="range"
          min={range.min}
          max={range.max}
          step={range.step}
          value={valor}
          onChange={e => {
            setValor(Number(e.target.value));
            markStarted();
          }}
          className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[var(--sr-navy)] sr-focus-ring"
          aria-describedby={idResumo}
          aria-valuemin={range.min}
          aria-valuemax={range.max}
          aria-valuenow={valor}
        />
      </div>
      <div className="flex justify-between text-[10px] text-gray-600 mb-4">
        <span>R$ {formatBRL(range.min)}</span>
        <span>R$ {formatBRL(range.max)}</span>
      </div>

      <div
        id={idResumo}
        aria-live="polite"
        aria-atomic="true"
        className="bg-[var(--sr-cream)] rounded-[var(--radius-md-token)] p-4 mb-5"
      >
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-600">Crédito</span>
          <span className="text-sm font-bold text-[var(--sr-navy)] sr-num">
            R$ {formatBRL(valor)}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-600">Parcela estimada</span>
          <span className="text-sm font-bold text-[var(--sr-navy)] sr-num">
            ~R$ {formatBRL(parcela)}/mês
          </span>
        </div>
      </div>

      <label htmlFor={idNome} className="sr-only">
        Seu nome (opcional)
      </label>
      <input
        id={idNome}
        name="nome"
        type="text"
        autoComplete="name"
        placeholder="Seu nome (opcional)"
        value={nome}
        onChange={e => setNome(e.target.value)}
        className="w-full border border-gray-200 rounded-[var(--radius-sm-token)] px-4 py-3 text-sm mb-4 focus:border-[var(--sr-navy)] focus:ring-1 focus:ring-[var(--sr-navy)] outline-none transition-colors"
      />

      <button
        type="button"
        onClick={handleSimular}
        className="w-full inline-flex items-center justify-center gap-2 bg-[var(--sr-navy)] text-white font-semibold py-3.5 rounded-[var(--radius-sm-token)] text-sm hover:bg-[var(--sr-navy-deep)] active:scale-[0.97] transition-all shadow-[var(--shadow-cta-navy)] sr-focus-ring"
      >
        Simular meu consórcio
      </button>
      <p className="text-center text-[10px] text-gray-600 mt-2">
        *Valores aproximados. Bazilio retorna em minutos no WhatsApp.
      </p>
    </div>
  );
}
